import { Injectable, computed, inject, signal } from '@angular/core';

import { CanvasEditorStore } from '@components/canvas';

import { DASHBOARD_CANVAS_CATALOG } from '../enums/dashboard-widget-sizing.enum';
import { DEFAULT_DASHBOARD_LAYOUT } from '../data/user-preferences.data';
import type { DashboardMenuEntry, DashboardWidget, PersistedReport } from '../types';

import { UserPreferencesService } from './user-preferences.service';

/** The Dashboard dropdown is a menu, not a listing; past a handful it stops being scannable. */
const MAX_PINNED_DASHBOARDS = 5;

/**
 * Owns which dashboard is on screen and what appears under Dashboard in the sidebar.
 *
 * The canvas store reads its layout through `persistence.saved()`, which is a `computed()` - so
 * pointing that at the active dashboard is all it takes for selecting one to swap the canvas,
 * including while editing. A dashboard is a favourited report with `format: 'dashboard'`; a null
 * id means the user's own dashboard.
 */
@Injectable({ providedIn: 'root' })
export class DashboardBuilderService {
  private readonly preferences = inject(UserPreferencesService);

  private readonly _activeId = signal<string | null>(null);
  readonly activeId = this._activeId.asReadonly();

  private readonly pinnedReports = computed<PersistedReport[]>(() => {
    const reports = this.preferences
      .reports()
      .filter((r) => r.isFavourite && r.format === 'dashboard');
    const order = this.preferences.get('dashboardOrder');
    const rank = new Map(order.map((id, index) => [id, index]));
    // Anything the user has not explicitly ordered sorts after what they have.
    return reports
      .slice()
      .sort(
        (a, b) =>
          (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER),
      )
      .slice(0, MAX_PINNED_DASHBOARDS);
  });

  readonly menuEntries = computed<DashboardMenuEntry[]>(() => [
    { id: null, name: 'My Dashboard', removable: false },
    ...this.pinnedReports().map((report) => ({
      id: report.id,
      name: report.name,
      removable: true,
    })),
  ]);

  /** True once there is something to choose between - below that Dashboard stays a plain link. */
  readonly hasPinnedDashboards = computed<boolean>(() => this.pinnedReports().length > 0);

  readonly activeName = computed<string>(
    () => this.menuEntries().find((entry) => entry.id === this.activeId())?.name ?? 'My Dashboard',
  );

  readonly store = new CanvasEditorStore<DashboardWidget>({
    persistence: {
      saved: () => {
        const id = this._activeId();
        if (!id) return this.preferences.dashboardLayout();
        return this.preferences.reports().find((report) => report.id === id)?.widgets ?? [];
      },
      defaults: () => DEFAULT_DASHBOARD_LAYOUT,
      persist: (layout) => {
        const id = this._activeId();
        if (!id) {
          this.preferences.set('dashboardLayout', [...layout]);
          return;
        }
        this.preferences.set(
          'reports',
          this.preferences
            .get('reports')
            .map((report) =>
              report.id === id
                ? { ...report, widgets: [...layout], updatedAt: new Date().toISOString() }
                : report,
            ),
        );
      },
    },
    kindCatalog: DASHBOARD_CANVAS_CATALOG,
    cols: 12,
  });

  /**
   * An id that is not pinned falls back to the user's own dashboard rather than rendering an empty
   * canvas - a stale bookmark should land somewhere real.
   */
  select(id: string | null): void {
    const known = id !== null && this.pinnedReports().some((report) => report.id === id);
    this._activeId.set(known ? id : null);
  }

  setOrder(ids: ReadonlyArray<string>): void {
    this.preferences.set('dashboardOrder', [...ids]);
  }

  /** Unpinning is just unfavouriting - the report itself is untouched and stays in Reports. */
  unpin(id: string): void {
    this.preferences.set(
      'reports',
      this.preferences
        .get('reports')
        .map((report) => (report.id === id ? { ...report, isFavourite: false } : report)),
    );
    this.preferences.set(
      'dashboardOrder',
      this.preferences.get('dashboardOrder').filter((entry) => entry !== id),
    );
    if (this._activeId() === id) this._activeId.set(null);
  }
}
